import {
  Injectable,
  inject,
  signal,
  computed,
  ApplicationRef,
  EnvironmentInjector,
  createComponent,
  createEnvironmentInjector,
  ComponentRef,
} from '@angular/core';
import { DOCUMENT } from '@angular/common';

@Injectable({ providedIn: 'root' })
export class PipTimerService {
  private readonly appRef = inject(ApplicationRef);
  private readonly injector = inject(EnvironmentInjector);

  private pipWindow: Window | null = null;
  private componentRef: ComponentRef<unknown> | null = null;

  readonly isPipActive = signal(false);
  readonly isSupported = computed(
    () => typeof window !== 'undefined' && 'documentPictureInPicture' in window
  );

  async openPip(): Promise<void> {
    if (!this.isSupported()) throw new Error('Document PiP not supported');
    if (this.pipWindow) { this.pipWindow.focus(); return; }

    // Lazy-load to keep initial bundle lean
    const { PipTimerComponent, PIP_TIMER_CSS } = await import(
      '../../shared/components/pip-timer/pip-timer.component'
    );

    const pipWindow = await (window as any).documentPictureInPicture.requestWindow({
      width: 300,
      height: 248,
    });

    this.copyStylesToWindow(pipWindow);
    this.syncBodyClass(pipWindow);

    // Inject PiP component styles directly — Angular's style nonce can prevent
    // them from appearing in document.styleSheets, so we inject explicitly.
    const pipStyle = pipWindow.document.createElement('style');
    pipStyle.id = 'pip-timer-explicit-styles';
    pipStyle.textContent = PIP_TIMER_CSS;
    pipWindow.document.head.appendChild(pipStyle);

    const root = pipWindow.document.createElement('div');
    root.id = 'pip-root';
    pipWindow.document.body.appendChild(root);
    pipWindow.document.body.style.cssText = 'margin:0;padding:0;overflow:hidden;height:100vh;';

    // Override DOCUMENT token so Angular creates DOM nodes in the PiP window
    const pipInjector = createEnvironmentInjector(
      [{ provide: DOCUMENT, useValue: pipWindow.document }],
      this.injector
    );

    this.componentRef = createComponent(PipTimerComponent, {
      hostElement: root,
      environmentInjector: pipInjector,
    });
    this.appRef.attachView(this.componentRef.hostView);
    this.componentRef.changeDetectorRef.detectChanges();

    this.pipWindow = pipWindow;
    this.isPipActive.set(true);

    pipWindow.addEventListener('pagehide', () => this.cleanup(), { once: true });

    // Watch for theme/class changes in the main window and sync to PiP.
    // Theme lives on <html data-theme>; legacy callers may also toggle a body class.
    const bodyObserver = new MutationObserver(() => this.syncBodyClass(pipWindow));
    bodyObserver.observe(document.body, { attributes: true, attributeFilter: ['class'] });

    const themeObserver = new MutationObserver(() => this.syncTheme(pipWindow));
    themeObserver.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ['data-theme', 'dir', 'lang']
    });

    pipWindow.addEventListener('pagehide', () => {
      bodyObserver.disconnect();
      themeObserver.disconnect();
    }, { once: true });
  }

  closePip(): void {
    this.pipWindow?.close();
  }

  private cleanup(): void {
    if (this.componentRef) {
      this.appRef.detachView(this.componentRef.hostView);
      this.componentRef.destroy();
      this.componentRef = null;
    }
    this.pipWindow = null;
    this.isPipActive.set(false);
  }

  private copyStylesToWindow(pipWindow: Window): void {
    Array.from(document.styleSheets).forEach(sheet => {
      try {
        const css = Array.from(sheet.cssRules).map(r => r.cssText).join('');
        const style = pipWindow.document.createElement('style');
        style.textContent = css;
        pipWindow.document.head.appendChild(style);
      } catch {
        // Cross-origin sheet — link it directly
        if (sheet.href) {
          const link = pipWindow.document.createElement('link');
          link.rel = 'stylesheet';
          link.href = sheet.href;
          pipWindow.document.head.appendChild(link);
        }
      }
    });

    // Copy lang/dir/data-theme from main document so CSS variables resolve correctly
    this.syncTheme(pipWindow);
  }

  private syncBodyClass(pipWindow: Window): void {
    pipWindow.document.body.className = document.body.className;
  }

  /** Mirror <html lang/dir/data-theme> from the main document into the PiP window. */
  private syncTheme(pipWindow: Window): void {
    const src = document.documentElement;
    const dst = pipWindow.document.documentElement;
    dst.lang = src.lang;
    dst.dir = src.dir;
    const theme = src.getAttribute('data-theme');
    if (theme) dst.setAttribute('data-theme', theme);
    else dst.removeAttribute('data-theme');
  }
}
